import {
  type AcquiredPropertyFilters,
  type AcquiredPropertySearch,
  type AcquiredListView,
} from "../types";

export const EMPTY_FILTERS: AcquiredPropertyFilters = {
  identificationName: "",
  prefectureCode: "",
  location: "",
  chibanKaokuNumber: "",
  acquiredDateFrom: "",
  acquiredDateTo: "",
};

const FILTER_KEYS: Record<keyof AcquiredPropertyFilters, string> = {
  identificationName: "identification_name",
  prefectureCode: "prefecture_code",
  location: "location",
  chibanKaokuNumber: "chiban_kaoku_number",
  acquiredDateFrom: "acquired_date_from",
  acquiredDateTo: "acquired_date_to",
};

/** 取得ユーザーやページ指定は、空欄検索を許可する条件には含めない。 */
export const hasSearchFilters = (filters: AcquiredPropertyFilters): boolean =>
  (Object.keys(FILTER_KEYS) as Array<keyof AcquiredPropertyFilters>).some(
    (key) => filters[key].trim() !== ""
  );

const nonnegativeInteger = (value: string | null, fallback: number): number => {
  if (value === null || !/^\d+$/.test(value)) return fallback;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : fallback;
};

/** URLの共有指定は表示条件のみ 認可はAPIで行う */
export const readSearchState = (
  params: URLSearchParams,
  isSharable: boolean
): AcquiredPropertySearch => {
  // 既存一覧と同じ本人範囲から開始し、共有ONでは明示された絞り込みを維持する
  const requestedScope = params.get("scope");
  const scope = !isSharable
    ? "self"
    : requestedScope === "self" ||
      requestedScope === "other" ||
      requestedScope === "all"
    ? requestedScope
    : "self";
  const pageSize = nonnegativeInteger(params.get("page_size"), 50);
  const filters = { ...EMPTY_FILTERS };
  for (const key of Object.keys(FILTER_KEYS) as Array<
    keyof AcquiredPropertyFilters
  >) {
    filters[key] = (params.get(FILTER_KEYS[key]) ?? "").trim();
  }
  return {
    ...filters,
    scope,
    page: nonnegativeInteger(params.get("page"), 0),
    pageSize: [25, 50, 100].includes(pageSize) ? pageSize : 50,
  };
};

export const writeSearchState = (
  state: AcquiredPropertySearch,
  view: AcquiredListView = "requests"
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("view", view);
  for (const key of Object.keys(FILTER_KEYS) as Array<
    keyof AcquiredPropertyFilters
  >) {
    const value = state[key].trim();
    if (value) params.set(FILTER_KEYS[key], value);
  }
  params.set("scope", state.scope);
  params.set("page", String(state.page));
  params.set("page_size", String(state.pageSize));
  return params;
};

export const makeSearchParams = (
  state: AcquiredPropertySearch
): Record<string, string | number | boolean> => {
  const result: Record<string, string | number | boolean> = {
    limit: state.pageSize,
    offset: state.page * state.pageSize,
  };
  for (const key of Object.keys(FILTER_KEYS) as Array<
    keyof AcquiredPropertyFilters
  >) {
    if (state[key]) result[key] = state[key];
  }
  if (state.scope !== "all")
    result.isOtherUserResource = state.scope === "other";
  return result;
};

export const getListReturnPath = (state: unknown, userId: string): string => {
  if (typeof state !== "object" || state === null) return "/acquirebooklist";
  const value = state as Record<string, unknown>;
  if (
    value.acquiredListUserId !== userId ||
    typeof value.acquiredListReturnTo !== "string" ||
    !/^\/acquirebooklist(?:\?[^#]*)?$/.test(value.acquiredListReturnTo)
  )
    return "/acquirebooklist";
  return value.acquiredListReturnTo;
};
